import { Button, Code, CustomCheckbox } from "@cynthai/ui";
import { useBlocker } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { triggerBrowserDownload } from "@/core/lib/api";
import { m } from "@/paraglide/messages";

export function RecoveryCodes({
  codes,
  onContinue,
}: {
  readonly codes: readonly string[];
  readonly onContinue: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const text = codes.join("\n");
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useBlocker({ shouldBlockFn: () => true, enableBeforeUnload: true });

  async function copyCodes() {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  }

  return (
    <div className="space-y-4">
      <h1
        ref={titleRef}
        tabIndex={-1}
        className="text-text-primary text-2xl font-semibold"
      >
        {m.two_fa_recovery_title()}
      </h1>
      <p className="text-text-secondary text-sm">
        {m.two_fa_recovery_description()}
      </p>
      <ul className="grid grid-cols-2 gap-2">
        {codes.map((code) => (
          <li key={code}>
            <Code size="sm">{code}</Code>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-2">
        <Button variant="ghost" onPress={copyCodes}>
          {m.two_fa_recovery_copy()}
        </Button>
        <Button
          variant="ghost"
          onPress={() =>
            triggerBrowserDownload(
              new Blob([text], { type: "text/plain;charset=utf-8" }),
              "cynthai-recovery-codes.txt",
            )
          }
        >
          {m.two_fa_recovery_download()}
        </Button>
      </div>
      {copyStatus === "copied" && (
        <output className="text-text-secondary block text-sm">
          {m.two_fa_recovery_copied()}
        </output>
      )}
      {copyStatus === "failed" && (
        <p role="alert" className="text-danger text-sm">
          {m.two_fa_recovery_copy_failed()}
        </p>
      )}
      <CustomCheckbox isSelected={saved} onChange={setSaved}>
        {m.two_fa_recovery_saved()}
      </CustomCheckbox>
      <Button
        color="primary"
        size="lg"
        className="w-full"
        isDisabled={!saved}
        onPress={() => {
          if (saved) onContinue();
        }}
      >
        {m.continue()}
      </Button>
    </div>
  );
}
